import React, { useCallback, useEffect, useState } from 'react';
import {
    Paper, Typography, Box, Button, TextField, Grid, Switch, FormControlLabel, Divider,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
    getBillingSettingsService, saveBillingSettingsService, getDqtRateService, setDqtRateService,
} from '../../../../services/billing.service';
import { toastSuccess } from '../../../../utils/utils';

/**
 * PaymentSettingsTab — how buyers pay, and what a DQ Token is worth. One Save.
 *
 * - Wallet payments (Apple Pay, Google Pay, Link): off means checkout is card only.
 * - Bank transfer: offered only when turned on AND the bank details a buyer needs
 *   are filled in. The details below are exactly what the buyer is shown.
 * - DQ Token rate (moved here from its own tab, 20 Oct 2026): one global rate, kept
 *   as a history — setting one adds a record and never edits the old one. It turns
 *   USD into DQT for purchases and grants, and prices partners' licence tiers in DQT.
 *   A change applies from now on; balances partners hold are never adjusted. Leave
 *   the field empty to keep the current rate. A new rate is confirmed before saving.
 */

const FIELDS = [
    { key: 'bank_name', note: true,       label: 'Bank name',       required: true },
    { key: 'account_name', note: true,    label: 'Account name',    required: true },
    { key: 'account_number', note: true,  label: 'Account number',  required: true },
    { key: 'swift_bic', note: true,       label: 'SWIFT / BIC',     required: true },
    { key: 'bank_code', note: true,       label: 'Bank code' },
    { key: 'bank_address', note: true,   label: 'Bank address',    multiline: true },
    { key: 'support_email',   label: 'Support email',   help: 'Shown under "Need help?", and where buyers may email proof.' },
    { key: 'support_phone',   label: 'Support phone' },
    { key: 'processing_note', label: 'Processing note', multiline: true, help: 'Shown under "Important", e.g. "Payments are usually reflected within 1–2 business days after we receive the funds."' },
];

// Every bank detail also has an optional note, shown beside it in brackets.
const noteKey = (key) => `${key}_note`;
const EMPTY_WIRE = FIELDS.reduce(
    (acc, f) => ({ ...acc, [f.key]: '', ...(f.note ? { [noteKey(f.key)]: '' } : {}) }),
    { enabled: false },
);

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const PaymentSettingsTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [walletsEnabled, setWalletsEnabled] = useState(false);
    const [wire, setWire] = useState(EMPTY_WIRE);
    const [meta, setMeta] = useState({ updated_by: null, updated_at: null });
    // DQ Token rate
    const [rate, setRate] = useState(null);
    const [history, setHistory] = useState([]);
    const [newRate, setNewRate] = useState('');
    const [rateNote, setRateNote] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);

    const apply = (data) => {
        if (!data) return;
        setWalletsEnabled(!!data.wallets_enabled);
        setWire({ ...EMPTY_WIRE, ...(data.wire_transfer || {}) });
        setMeta({ updated_by: data.updated_by, updated_at: data.updated_at });
    };

    const applyRate = (data) => {
        setRate(data?.rate || null);
        setHistory(data?.history || []);
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [settings, rates] = await Promise.all([getBillingSettingsService(), getDqtRateService()]);
            apply(settings?.data);
            applyRate(rates?.data);
        } catch (err) {
            // invokeApi has already shown the error.
            console.error('[PaymentSettingsTab] load failed:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const missing = FIELDS.filter((f) => f.required && !String(wire[f.key] || '').trim()).map((f) => f.label);
    const rateValue = Number(newRate);
    const rateEntered = newRate !== '';
    const rateValid = rateEntered && Number.isFinite(rateValue) && rateValue > 0;
    const canSave = !saving && !(wire.enabled && missing.length > 0) && (!rateEntered || rateValid);

    /** Save everything: the payment settings, and the new rate when one was entered. */
    const save = async () => {
        setSaving(true);
        try {
            const res = await saveBillingSettingsService({ wallets_enabled: walletsEnabled, wire_transfer: wire });
            apply(res?.data);
            if (rateValid) {
                await setDqtRateService({ usd_per_dqt: rateValue, note: rateNote.trim() });
                applyRate((await getDqtRateService())?.data);
                setNewRate('');
                setRateNote('');
            }
            toastSuccess(rateValid ? `Payment settings saved. DQ Token rate set to US$${rateValue} per DQT.` : 'Payment settings saved.');
            setConfirmOpen(false);
        } catch (err) {
            // invokeApi has already shown the API's reason (e.g. missing bank details).
            console.error('[PaymentSettingsTab] save failed:', err);
        } finally {
            setSaving(false);
        }
    };

    // A rate change is confirmed first: it changes what partners pay from now on.
    const handleSave = () => (rateValid ? setConfirmOpen(true) : save());

    if (loading) return <Box p={4} display="flex" justifyContent="center"><CircularProgress/></Box>;

    return (
        <Box>
            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Paper style={{ padding: 24 }}>
                        <Typography variant="overline" color="textSecondary">Card checkout</Typography>
                        <FormControlLabel
                            style={{ display: 'flex', marginTop: 8 }}
                            control={<Switch checked={walletsEnabled} onChange={(e) => { const { checked } = e.target; setWalletsEnabled(checked); }} color="primary"/>}
                            label={<Typography style={{ fontWeight: 600 }}>Wallet payments</Typography>}
                        />
                        <Typography variant="body2" color="textSecondary">
                            Apple Pay, Google Pay and Link. When off, buyers can pay by card only.
                        </Typography>

                        <Divider style={{ margin: '20px 0' }}/>

                        <Typography variant="overline" color="textSecondary">Bank transfer</Typography>
                        <FormControlLabel
                            style={{ display: 'flex', marginTop: 8 }}
                            control={<Switch checked={!!wire.enabled} onChange={(e) => { const { checked } = e.target; setWire((w) => ({ ...w, enabled: checked })); }} color="primary"/>}
                            label={<Typography style={{ fontWeight: 600 }}>Accept bank transfers</Typography>}
                        />
                        <Typography variant="body2" color="textSecondary">
                            Buyers pay to the account on the right, quoting their invoice number, then upload proof.
                            Approve or reject transfers in the Bank Transfers tab.
                        </Typography>
                        {wire.enabled && missing.length > 0 && (
                            <Typography variant="body2" style={{ color: '#b45309', marginTop: 12 }}>
                                Fill in {missing.join(', ')} before saving — buyers cannot pay without them.
                            </Typography>
                        )}

                        <Divider style={{ margin: '20px 0' }}/>

                        <Typography variant="overline" color="textSecondary">DQ Token rate</Typography>
                        {rate ? (
                            <>
                                <Typography variant="h5" style={{ fontWeight: 700, marginTop: 4 }}>1 DQT = US${rate.usd_per_dqt}</Typography>
                                <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                                    Since {fmtDate(rate.effective_from)}{rate.set_by?.label ? ` · set by ${rate.set_by.label}` : ''}
                                </Typography>
                            </>
                        ) : (
                            <Typography variant="body2" style={{ fontWeight: 700, marginTop: 4, color: '#b45309' }}>
                                No rate set — DQ Tokens cannot be bought or granted until one is.
                            </Typography>
                        )}
                        <TextField
                            label="New rate: US$ per 1 DQT"
                            type="number"
                            value={newRate}
                            onChange={(e) => setNewRate(e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            error={rateEntered && !rateValid}
                            helperText={rateEntered && !rateValid ? 'Must be greater than 0' : 'Leave empty to keep the current rate.'}
                            fullWidth variant="outlined" size="small"
                            style={{ marginTop: 16 }}
                        />
                        {rateEntered && (
                            <TextField
                                label="Why the rate is changing (optional)"
                                value={rateNote}
                                onChange={(e) => setRateNote(e.target.value)}
                                fullWidth variant="outlined" size="small"
                                style={{ marginTop: 8 }}
                            />
                        )}
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                            Applies to purchases, grants and partners’ licence prices from now on. Balances partners already hold are not changed.
                        </Typography>

                        <Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 20 }}>
                            Payment settings last saved {fmtDate(meta.updated_at)}{meta.updated_by?.label ? ` by ${meta.updated_by.label}` : ''}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Paper style={{ padding: 24 }}>
                        <Typography variant="overline" color="textSecondary">Bank details shown to buyers</Typography>
                        <Grid container spacing={2} style={{ marginTop: 4 }}>
                            {FIELDS.map((f) => (
                                <Grid item xs={12} sm={f.multiline ? 12 : 6} key={f.key}>
                                    <TextField
                                        fullWidth variant="outlined" size="small"
                                        label={f.label} required={f.required && !!wire.enabled}
                                        value={wire[f.key] || ''}
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setWire((w) => ({ ...w, [f.key]: value }));
                                        }}
                                        multiline={!!f.multiline} minRows={f.multiline ? 2 : undefined}
                                        inputProps={{ maxLength: 500 }}
                                        helperText={f.help || ' '}
                                    />
                                    {f.note && (
                                        <TextField
                                            fullWidth variant="outlined" size="small"
                                            style={{ marginTop: 4 }}
                                            label={`${f.label} note (optional)`}
                                            placeholder="e.g. Use when sending money from outside Singapore"
                                            value={wire[noteKey(f.key)] || ''}
                                            onChange={(e) => {
                                                const { value } = e.target;
                                                setWire((w) => ({ ...w, [noteKey(f.key)]: value }));
                                            }}
                                            inputProps={{ maxLength: 500 }}
                                            helperText="Shown in brackets beside this field."
                                        />
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {/* One Save for everything on this tab */}
            <Box display="flex" justifyContent="flex-end" mt={2}>
                <Button variant="contained" color="primary" onClick={handleSave} disabled={!canSave}>
                    {saving ? <CircularProgress size={20} style={{ color: '#fff' }}/> : 'Save settings'}
                </Button>
            </Box>

            {/* DQ Token rate history */}
            <Paper style={{ padding: 24, marginTop: 24 }}>
                <Typography variant="overline" color="textSecondary">DQ Token rate history</Typography>
                <Divider style={{ margin: '8px 0 12px' }}/>
                {history.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No rates recorded yet.</Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>US$ per DQT</TableCell>
                                    <TableCell>Effective from</TableCell>
                                    <TableCell>Set by</TableCell>
                                    <TableCell>Note</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {history.map((r, i) => (
                                    <TableRow key={r._id || i} selected={i === 0}>
                                        <TableCell style={{ fontWeight: i === 0 ? 700 : 400 }}>{r.usd_per_dqt}</TableCell>
                                        <TableCell>{fmtDate(r.effective_from)}</TableCell>
                                        <TableCell>{r.set_by?.label || r.set_by?.email || '—'}</TableCell>
                                        <TableCell>{r.note || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Save and change the DQ Token rate?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        From now on, <strong>US${rateValid ? rateValue : '—'}</strong> will buy <strong>1 DQT</strong>.
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 12 }}>
                        {rate ? `Current rate: US$${rate.usd_per_dqt} per DQT. ` : ''}
                        Partners’ licence prices in DQT follow the new rate. Balances partners already hold are not changed.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={save} color="primary" variant="contained" disabled={saving}
                        startIcon={saving ? <CircularProgress size={14} color="inherit"/> : null}>
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PaymentSettingsTab;
